import { useEffect, useState, useMemo } from 'react';
import {
  subscribeToAlert,
  subscribeToDismissed,
  markAlertAsDismissed,
  subscribeToPendingReply,
  markReplyAsHandled,
} from '@/lib/alertsSync';

/**
 * Hook para gestionar los avisos del workspace
 * @param {string} workspaceId - ID del workspace
 * @param {string} profileKey - Nombre/clave del perfil actual (usado para el
 *   path existente de alertsDismissed_profile_{sanitizeProfileKey(profileKey)},
 *   sin cambios de path)
 * @param {string} [profileId] - ID canónico real del perfil activo (UUID de
 *   accountMemberships/config.profiles). Se escribe en el documento dismissed
 *   para que Firestore Rules pueda verificar server-side (isWorkerAssigned/
 *   isCanonicalAdminOf) la propiedad del dismiss, sin depender de la
 *   sanitización client-side del docId. Si no se provee, cae a profileKey
 *   (comportamiento legacy, no rompe llamadas existentes).
 * @returns {Object} - { currentAlert, showAlert, handleDismiss, currentReply, handleDismissReply }
 */
export function useAlerts(workspaceId, profileKey, profileId) {

  const [currentAlert, setCurrentAlert] = useState(null);
  const [dismissedAlertId, setDismissedAlertId] = useState(null);
  const [currentReply, setCurrentReply] = useState(null);

  // Suscribirse al aviso global del workspace
  useEffect(() => {
    if (!workspaceId) return;

    const unsub = subscribeToAlert(workspaceId, (alert) => {
      console.log('[useAlerts] Current alert updated:', alert);
      setCurrentAlert(alert);
    });

    return unsub;
  }, [workspaceId]);

  // Suscribirse a los avisos descartados por el perfil
  useEffect(() => {
    if (!workspaceId || !profileKey) return;

    const unsub = subscribeToDismissed(workspaceId, profileKey, (dismissed) => {
      console.log('[useAlerts] Dismissed alert ID updated for profile:', profileKey, dismissed?.lastDismissedAlertId);
      setDismissedAlertId(dismissed?.lastDismissedAlertId || null);
    });

    return unsub;
  }, [workspaceId, profileKey]);

  // Suscribirse a la reacción dirigida (ack/reply) pendiente para el perfil activo.
  // Se limpia y vuelve a suscribir cuando cambia workspaceId o profileKey (cambio de perfil).
  useEffect(() => {
    if (!workspaceId || !profileKey) {
      setCurrentReply(null);
      return;
    }

    const unsub = subscribeToPendingReply(workspaceId, profileKey, (reply) => {
      console.log('[useAlerts] Pending reply updated for profile:', profileKey, reply);
      setCurrentReply(reply);
    });

    return () => {
      unsub();
      setCurrentReply(null);
    };
  }, [workspaceId, profileKey]);

  // Calcular derivadamente si mostrar el aviso
  // showAlert = true si hay un aviso Y su ID es diferente al último descartado
  const showAlert = useMemo(() => {
    if (!currentAlert || !currentAlert.alertId) {
      return false;
    }
    return currentAlert.alertId !== dismissedAlertId;
  }, [currentAlert, dismissedAlertId]);

  // Manejador para descartar el aviso
  const handleDismiss = async () => {
    if (!workspaceId || !profileKey || !currentAlert?.alertId) {
      return;
    }

    try {
      // Se propaga profileId canónico (real, no derivado del sufijo del
      // docId) para que Firestore Rules pueda validar server-side la
      // propiedad del dismiss (isWorkerAssigned/isCanonicalAdminOf).
      await markAlertAsDismissed(workspaceId, profileKey, currentAlert.alertId, profileId);
      console.log('[useAlerts] Alert dismissed for profile:', profileKey, 'profileId:', profileId);
    } catch (error) {
      console.error('[useAlerts] Error dismissing alert:', error);
      throw error;
    }
  };

  // Manejador para cerrar/gestionar la reply pendiente actual.
  // Al marcarla como handled, la suscripción FIFO entrega automáticamente la siguiente.
  const handleDismissReply = async () => {
    if (!workspaceId || !currentReply?.id) {
      return;
    }

    try {
      await markReplyAsHandled(workspaceId, currentReply.id);
      console.log('[useAlerts] Reply dismissed:', currentReply.id);
    } catch (error) {
      console.error('[useAlerts] Error dismissing reply:', error);
      throw error;
    }
  };

  return {
    currentAlert,
    showAlert,
    handleDismiss,
    currentReply,
    handleDismissReply,
  };
}
